import { Box, Typography } from '@mui/material'
import { PatientUser } from '~/shared/types'

import { useCallback, useState } from 'react'
import { BehindProviderFeatureAccessCheck, SectionCard } from '~/shared/ui'
import EditAddressForm from './EditAddressForm'
import EditInsuranceForm from './EditInsuranceForm'
import EditMedicalEquipment from './EditMedicalEquipment'
import EditPatientCareTeam from './EditPatientCareTeam'
import EditPatientForm from './EditPatientForm'
import type { FC, ReactNode } from 'react'
import { useCreateOrUpdateInsuranceAddress } from './PersonInsuranceService'
import { patientsSlice } from '~/entities/person'
import { useFeatureAccess } from '~/shared/hooks/useFeatureAccess'
import { useAppDispatch } from '~/app/store/hooks'
import { logger } from '~/shared/lib/logger'

interface EditPatientProps {
  patient: PatientUser
}

/** Card title for sections whose heading belongs to this shell (the
 * Insurance / Care Team / Medical Equipment children title themselves). */
const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
    {children}
  </Typography>
)

/**
 * Design-refresh Profile tab, patient mode: the legacy EditPatient's submit
 * handlers and section forms verbatim, each section framed in a SectionCard.
 */
const EditPatient: FC<EditPatientProps> = props => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState<boolean>(false)
  const featureAccess = useFeatureAccess()

  const { mutateAsync: handleCreateOrUpdateInsuranceAddress } = useCreateOrUpdateInsuranceAddress()

  // PATCH the full first-address row to /insurance/member/{id}. The nested
  // address serializer (and the generated body schema) require
  // street_address/city/state/zipcode, so the complete row is sent rather
  // than a diffed subset; the backend merges per-field, so re-sending
  // unchanged fields is a no-op.
  const handleAddressSubmit = useCallback(
    async (values: PatientUser) => {
      const insuranceInfo = values.person.insurance_info
      if (insuranceInfo == null) return

      try {
        setLoading(true)
        await handleCreateOrUpdateInsuranceAddress({
          insuranceInfo: {
            id: insuranceInfo.id,
            addresses: insuranceInfo.addresses?.slice(0, 1),
          },
        })
        // Refresh Redux from the server so the address landed in
        // state.patients/users matches what the backend persisted.
        await dispatch(patientsSlice.thunks.getPatient(props.patient.id))
      } catch (e) {
        logger.error('[Patient] save address', e)
      } finally {
        setLoading(false)
      }
    },
    [handleCreateOrUpdateInsuranceAddress, dispatch, props.patient.id],
  )

  const handlePatientDetailsSubmit = useCallback(
    async (values: PatientUser) => {
      const { id, person, first_name, last_name, phone_number, email } = values
      try {
        setLoading(true)
        if (person.insurance_info) {
          const newPerson = {
            ...person,
            avatar: undefined,
            avatar_large: undefined,
            gender: Array.isArray(person.gender) && !person.gender.length ? null : person.gender,
          }
          await dispatch(
            patientsSlice.thunks.updatePatient({
              email,
              first_name,
              id,
              last_name,
              person: newPerson,
              phone_number,
            }),
          )
        }
      } catch (e) {
        logger.error('[Patient] save basic info', e)
      } finally {
        setLoading(false)
      }
    },
    [dispatch],
  )

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard>
        <SectionTitle>Basic Info</SectionTitle>
        <EditPatientForm
          handleSubmit={handlePatientDetailsSubmit}
          loading={loading}
          patient={props.patient}
        />
      </SectionCard>

      <SectionCard>
        <SectionTitle>Address</SectionTitle>
        <EditAddressForm
          handleSubmit={handleAddressSubmit}
          loading={loading}
          patient={props.patient}
        />
      </SectionCard>

      <BehindProviderFeatureAccessCheck
        feature="insurance_and_eligibility"
        featureAccess={featureAccess}
      >
        {/* EditInsuranceForm owns its loader/action (BFF) and the eligibility
            check; pass `userId` so it enables the patient-only branches
            (eligibility button, coverage-status update, Redux refresh). It
            titles itself, so the card adds no header. */}
        <SectionCard>
          <EditInsuranceForm person={props.patient.person} userId={props.patient.id} />
        </SectionCard>
      </BehindProviderFeatureAccessCheck>

      <BehindProviderFeatureAccessCheck feature="care_plans" featureAccess={featureAccess}>
        <SectionCard>
          <EditPatientCareTeam patient={props.patient} />
        </SectionCard>
      </BehindProviderFeatureAccessCheck>

      <SectionCard>
        <EditMedicalEquipment patient={props.patient} />
      </SectionCard>
    </Box>
  )
}

export default EditPatient
